import { useEffect, useMemo, useState } from 'react';

import type { IOrganizationMember } from '@/domain/entities/org-member';
import { normalizeRoleKey } from '@/domain/entities/role';
import type { IBusinessUser } from '@/domain/entities/user';
import { OrgStatus } from '@/domain/shared/shared';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { ErrorNotice } from '@/components/ui/error-notice';
import { OrganizationInviteMemberDialog, type InviteMemberResult } from './OrganizationInviteMemberDialog';

type MemberRoleState = {
  roleKey: string;
  permissions: string[];
};

type OrganizationMembersTabProps = {
  members: IOrganizationMember[];
  memberRolesById: Record<string, MemberRoleState>;
  usersById: Map<string, IBusinessUser>;
  currentMemberId?: string | null;
  canManageMembers: boolean;
  canManageOwnMembership?: boolean;
  canInviteMembers?: boolean;
  organizationName?: string;
  isInvitingMember?: boolean;
  onInviteMember?: (email: string) => Promise<InviteMemberResult>;
  onManageRole: (member: IOrganizationMember, memberLabel: string) => void;
  onSaveMemberStatus: (member: IOrganizationMember, status: OrgStatus) => Promise<void>;
  formatDate: (timestamp?: number) => string;
  isLoading: boolean;
  hasFetched: boolean;
  error: string | null;
  onRetry: () => void;
};

function getMemberStatusBadge(status: OrgStatus) {
  if (status === OrgStatus.Active) {
    return (
      <Badge variant="default" className="bg-green-600">
        Active
      </Badge>
    );
  }

  if (status === OrgStatus.Suspended) {
    return <Badge variant="secondary">Suspended</Badge>;
  }

  return <Badge variant="outline">Inactive</Badge>;
}

function formatRoleKey(roleKey?: string) {
  if (!roleKey) return 'No role';
  return normalizeRoleKey(roleKey);
}

function normalize(value: string) {
  return value.trim().toLowerCase();
}

export function OrganizationMembersTab({
  members,
  memberRolesById,
  usersById,
  currentMemberId,
  canManageMembers,
  canManageOwnMembership = false,
  canInviteMembers = false,
  organizationName,
  isInvitingMember = false,
  onInviteMember,
  onManageRole,
  onSaveMemberStatus,
  formatDate,
  isLoading,
  hasFetched,
  error,
  onRetry,
}: OrganizationMembersTabProps) {
  const [query, setQuery] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [memberStatusDraftById, setMemberStatusDraftById] = useState<Record<string, OrgStatus>>({});
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [statusSaveError, setStatusSaveError] = useState<string | null>(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const membersWithDetails = useMemo(() => {
    return members.map((member) => {
      const profile = usersById.get(member.memberID);
      const memberRole = memberRolesById[member.memberID];
      const fullName = profile
        ? `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim() || profile.email || member.memberID
        : member.memberID;

      return {
        member,
        profile,
        memberRole,
        fullName,
        email: profile?.email || 'Profile not loaded',
        roleLabel: formatRoleKey(memberRole?.roleKey ?? member.roleKey),
        permissionsCount: memberRole?.permissions.length ?? 0,
        effectiveStatus: memberStatusDraftById[member.memberID] ?? member.status,
      };
    });
  }, [memberRolesById, memberStatusDraftById, members, usersById]);

  const filteredMembers = useMemo(() => {
    const q = normalize(query);
    if (!q) return membersWithDetails;

    return membersWithDetails.filter(({ member, fullName, email, roleLabel }) => {
      return (
        normalize(fullName).includes(q) ||
        normalize(email).includes(q) ||
        normalize(member.memberID).includes(q) ||
        normalize(roleLabel).includes(q)
      );
    });
  }, [membersWithDetails, query]);

  useEffect(() => {
    if (filteredMembers.length === 0) {
      setSelectedMemberId('');
      return;
    }

    if (!selectedMemberId) {
      setSelectedMemberId(filteredMembers[0].member.memberID);
      return;
    }

    const isSelectedStillVisible = filteredMembers.some(({ member }) => member.memberID === selectedMemberId);
    if (!isSelectedStillVisible) {
      setSelectedMemberId(filteredMembers[0].member.memberID);
    }
  }, [filteredMembers, selectedMemberId]);

  const selectedMemberView = useMemo(
    () => membersWithDetails.find(({ member }) => member.memberID === selectedMemberId) ?? null,
    [membersWithDetails, selectedMemberId],
  );

  const selectedRolePermissions = selectedMemberView?.memberRole?.permissions ?? [];
  const selectedStatus = selectedMemberView?.effectiveStatus;
  const selectedRoleKey = (selectedMemberView?.memberRole?.roleKey ?? selectedMemberView?.member.roleKey ?? '').trim();
  const hasAssignedRole = selectedRoleKey.length > 0;
  const isSelfSelected = selectedMemberView?.member.memberID === currentMemberId;
  const selfManagementBlocked = isSelfSelected && !canManageOwnMembership;
  const selectedStatusChanged =
    !!selectedMemberView &&
    !selfManagementBlocked &&
    selectedMemberView.member.status !== selectedMemberView.effectiveStatus;

  const handleSaveSelectedStatus = async () => {
    if (!selectedMemberView || !selectedStatusChanged || selfManagementBlocked) return;

    setStatusSaveError(null);
    setIsSavingStatus(true);

    try {
      await onSaveMemberStatus(selectedMemberView.member, selectedMemberView.effectiveStatus);
      setMemberStatusDraftById((state) => {
        const next = { ...state };
        delete next[selectedMemberView.member.memberID];
        return next;
      });
    } catch (err) {
      const error = err as { message?: string };
      setStatusSaveError(error?.message || 'Failed to update membership status.');
    } finally {
      setIsSavingStatus(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">Organization Members</h3>
          <p className="text-muted-foreground text-sm">
            Select a member on the left to view details{canManageMembers ? ' and manage membership changes' : ''}.
          </p>
        </div>

        {canInviteMembers && onInviteMember ? (
          <Button type="button" onClick={() => setIsInviteModalOpen(true)}>
            Invite Member
          </Button>
        ) : null}
      </div>

      {!hasFetched || isLoading ? (
        <Card>
          <CardContent className="text-muted-foreground flex min-h-[200px] flex-col items-center justify-center gap-2 text-sm">
            <LoadingSpinner size={40} />
            <p>Loading organization members…</p>
          </CardContent>
        </Card>
      ) : error ? (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <ErrorNotice message={error} title="Failed to Load Organization Members" />
            <Button type="button" variant="outline" onClick={onRetry}>
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : members.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground text-sm">No members found.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
          <Card>
            <CardHeader>
              <CardTitle>Members</CardTitle>
              <CardDescription>Search by name, email, member id, or role.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search members…" />

              <div className="space-y-2">
                {filteredMembers.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No members match your search.</p>
                ) : (
                  filteredMembers.map(({ member, fullName, email, roleLabel, effectiveStatus }) => {
                    const isActive = member.memberID === selectedMemberId;
                    return (
                      <button
                        key={member.memberID}
                        type="button"
                        onClick={() => {
                          setSelectedMemberId(member.memberID);
                        }}
                        aria-pressed={isActive}
                        className={[
                          'w-full rounded-md border p-3 text-left transition',
                          isActive
                            ? 'bg-primary/5 border-white shadow-sm'
                            : 'hover:border-border/60 hover:bg-muted/40 border-transparent',
                        ].join(' ')}
                      >
                        <div className="space-y-2">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium">{fullName}</p>
                            <p className="text-muted-foreground truncate text-xs">{email}</p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="outline">{roleLabel}</Badge>
                            {getMemberStatusBadge(effectiveStatus)}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Member Details</CardTitle>
              <CardDescription>Review membership info and update assignable fields.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {!selectedMemberView ? (
                <p className="text-muted-foreground text-sm">Select a member to view details.</p>
              ) : (
                <>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{selectedMemberView.fullName}</p>
                      <p className="text-muted-foreground text-xs">{selectedMemberView.email}</p>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      {selectedStatusChanged ? (
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleSaveSelectedStatus}
                          disabled={!canManageMembers || isSavingStatus}
                        >
                          {isSavingStatus ? 'Saving…' : 'Save Status'}
                        </Button>
                      ) : null}
                      {selfManagementBlocked ? (
                        <p className="text-muted-foreground text-xs">Your membership is read-only here.</p>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => onManageRole(selectedMemberView.member, selectedMemberView.fullName)}
                          disabled={!canManageMembers}
                        >
                          {hasAssignedRole ? 'Manage Role' : 'Create Role'}
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Member ID</Label>
                      <Input value={selectedMemberView.member.memberID} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Organization ID</Label>
                      <Input value={selectedMemberView.member.id} readOnly />
                      <p className="text-muted-foreground text-xs">
                        This membership record uses the organization ID as its PK.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Created</Label>
                      <Input value={formatDate(selectedMemberView.member.createdAt)} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Updated</Label>
                      <Input value={formatDate(selectedMemberView.member.updatedAt)} readOnly />
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select
                        value={selectedStatus}
                        disabled={!canManageMembers || selfManagementBlocked}
                        onValueChange={(value) => {
                          if (!canManageMembers || selfManagementBlocked) return;
                          const nextStatus = value as OrgStatus;
                          setStatusSaveError(null);
                          setMemberStatusDraftById((state) => ({
                            ...state,
                            [selectedMemberView.member.memberID]: nextStatus,
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={OrgStatus.Active}>Active</SelectItem>
                          <SelectItem value={OrgStatus.Inactive}>Inactive</SelectItem>
                          <SelectItem value={OrgStatus.Suspended}>Suspended</SelectItem>
                        </SelectContent>
                      </Select>
                      {selfManagementBlocked ? (
                        <p className="text-muted-foreground text-xs">Your membership status cannot be changed here.</p>
                      ) : !canManageMembers ? (
                        <p className="text-muted-foreground text-xs">
                          You have read-only access to membership management.
                        </p>
                      ) : null}
                      {statusSaveError ? <p className="text-destructive text-xs">{statusSaveError}</p> : null}
                    </div>

                    <div className="space-y-2">
                      <Label>Role</Label>
                      <Input value={selectedMemberView.roleLabel} readOnly />
                      <p className="text-muted-foreground text-xs">
                        {selectedMemberView.permissionsCount} permission
                        {selectedMemberView.permissionsCount === 1 ? '' : 's'} in current role.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Role Permissions</Label>
                    <Textarea
                      readOnly
                      value={selectedRolePermissions.join('\n')}
                      rows={7}
                      className="max-h-56 min-h-40 overflow-y-auto font-mono text-xs"
                      placeholder="No role permissions selected."
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {onInviteMember ? (
        <OrganizationInviteMemberDialog
          open={isInviteModalOpen}
          organizationName={organizationName}
          isSubmitting={isInvitingMember}
          onOpenChange={setIsInviteModalOpen}
          onInviteMember={onInviteMember}
        />
      ) : null}
    </div>
  );
}
